import React, { useEffect, useState } from "react";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import { useForm } from "react-hook-form";
import { CustomButton } from "../../../atoms/CustomButton";
import { CustomInput } from "../../../atoms/CustomInput";
import { Switch } from "../../../atoms/Switch";
import useAuth from "../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import { useTranslation } from "react-i18next";
import FeatureUpgradePrompt from "../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../modals/PlansModal";

export interface CallBotSettingProps {
  initialEnabled?: boolean;
  initialInstructions?: string;
  onSubmit: (payload: {
    enabled: boolean;
    instructions: string;
  }) => Promise<void> | void;
  disabled: boolean;
}

const widgetSchema = Joi.object({
  enabled: Joi.boolean().required(),
  instructions: Joi.string()
    .allow("")
    .max(1000)
    .label("Additional Instructions"),
});

const CallBotSetting: React.FC<CallBotSettingProps> = ({
  initialEnabled = false,
  initialInstructions = "",
  onSubmit,
  disabled,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<{ enabled: boolean; instructions: string }>({
    defaultValues: {
      enabled: initialEnabled,
      instructions: initialInstructions,
    },
    resolver: joiResolver(widgetSchema),
  });

  const { user } = useAuth();
  const permissions = usePermissions(user);
  const hasAccess = permissions.isAtLeastTier(PlanTier.ENTERPRISE);
  const canWrite = permissions.can(Cap.LOYALTY, Level.WRITE);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    setValue("enabled", initialEnabled);
  }, [initialEnabled, setValue]);

  useEffect(() => {
    setValue("instructions", initialInstructions);
  }, [initialInstructions, setValue]);

  const onSubmitForm = handleSubmit(async (data) => {
    await onSubmit({
      enabled: data.enabled,
      instructions: data.instructions ?? "",
    });
  });

  if (!hasAccess) {
    return (
      <div className="w-full mt-6">
        <div className="flex justify-center mb-6">
          <img
            src="https://cdn.1cdn.app/application/UMD/25100114505060_callbot_louisebot_sum.png"
            alt={t("callbot.imageAlt") as string}
            className="w-[200px] rounded-xl"
          />
        </div>

        <div className="mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {t("callbot.title")}
          </h3>
        </div>

        <FeatureUpgradePrompt
          featureName={"CallBot"}
          featureDescription={t("callbot.premiumFeature.title") as string}
          benefits={[
            t("callbot.premiumFeature.benefit1") as string,
            t("callbot.premiumFeature.benefit2") as string,
            t("callbot.premiumFeature.benefit3") as string,
            t("callbot.premiumFeature.benefit4") as string,
          ]}
          onUpgrade={() => setShowUpgradeModal(true)}
          className="mb-5"
        />

        <PlansModal
          showPlansModal={showUpgradeModal}
          setShowPlansModal={setShowUpgradeModal}
          targetPlan={PlanTier.ENTERPRISE}
        />

        <div className="self-stretch h-px bg-gray-200 block mt-6" />
      </div>
    );
  }

  return (
    <div className="w-full mt-6">
      <div className="flex justify-center mb-6">
        <img
          src="https://cdn.1cdn.app/application/UMD/25100114505060_callbot_louisebot_sum.png"
          alt={t("callbot.imageAlt") as string}
          className="w-[200px] rounded-xl"
        />
      </div>

      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          {t("callbot.title")}
        </h3>
      </div>
      <form onSubmit={onSubmitForm}>
        <div className="mb-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <label
                htmlFor="callbot_enabled"
                className="text-gray-700 text-sm font-semibold font-figtree"
              >
                {t("callbot.enableToggle.label")}
              </label>
              <p className="text-gray-500 text-xs font-normal font-figtree mt-1">
                {t("callbot.enableToggle.description")}
              </p>
            </div>
            <Switch
              id="callbot_enabled"
              checked={watch("enabled")}
              disabled={disabled || !canWrite}
              onChange={async (next) => {
                setValue("enabled", next);
                await onSubmit({
                  enabled: next,
                  instructions: watch("instructions") ?? "",
                });
              }}
            />
          </div>
        </div>

        {watch("enabled") && (
          <div className="mb-4">
            <CustomInput
              label={t("callbot.instructions.label") as string}
              name="instructions"
              control={control}
              rows={8}
              maxLength={1000}
              disabled={!canWrite}
              placeholder={t("callbot.instructions.placeholder") as string}
              error={!!errors.instructions}
              helperText={errors.instructions?.message as string}
            />
            <p className="text-gray-500 text-xs font-normal font-figtree mt-2">
              ✓ {t("callbot.poweredBy")}{" "}
              <a
                href="https://louisebot.com"
                title="louisebot-callbot"
                target="_blank"
                aria-label="louisebot-callbot"
                rel="noreferrer"
                className="text-blue-600 hover:underline"
              >
                LouiseBot.com
              </a>{" "}
              {t("callbot.includedInPlan")}
            </p>
          </div>
        )}

        {watch("enabled") && (
          <CustomButton
            type="submit"
            disabled={disabled || !canWrite}
            isLoading={disabled}
            variant="primary"
            className="w-full"
            title={
              disabled ? t("callbot.buttons.saving") : t("callbot.buttons.save")
            }
          />
        )}
      </form>
    </div>
  );
};

export default React.memo(CallBotSetting);
